import React, { useState } from "react";
import { format, isToday, isTomorrow } from "date-fns";
import { pl } from "date-fns/locale";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import useMediaQuery from "@mui/material/useMediaQuery";
import EAButton from "../../shared/Buttons/EAButton";
import EAIconButton from "../../shared/Buttons/EAIconButton";
import SwimmingLessonCard from "../SwimmingGrid/SwimmingLessonCard";
import "../SwimmingGrid/style.scss";
import LoadableComponent from "../../../../features/Loading/LoadableComponent";
import { SwimmingLesson } from "../../../models/SwimmingScheduleData";
import "./style.scss";

interface IProps {
  columns: string[];
  visibleHours: string[];
  hasPastHours: boolean;
  onShowPastHours: () => void;
  onLessonClick: (lesson: SwimmingLesson) => void;
  findLessonsForSlot: (column: string, hour: string) => SwimmingLesson[];
  isDayColumnInRange: (dayKey: string) => boolean;
  loading?: boolean;
  firstAvailableDate?: Date | null;
  firstTimeLoading?: boolean;
  firstTimeChecked?: boolean;
  onGoToNearestDate?: () => void;
  isEmpty?: boolean;
}

const TIME_COLUMN_WIDTH = "72px";

const getDayHeader = (dateString: string) => {
  const date = new Date(dateString + "T00:00:00");
  const dayName = format(date, "EEEE", { locale: pl });
  const dateFormatted = format(date, "dd.MM");
  if (isToday(date)) {
    return `Dzisiaj ${dateFormatted}`;
  }
  if (isTomorrow(date)) {
    return `Jutro ${dateFormatted}`;
  }
  return `${dayName} ${dateFormatted}`;
};

const SwimmingWeeklyGrid: React.FC<IProps> = ({
  columns,
  visibleHours,
  hasPastHours,
  onShowPastHours,
  onLessonClick,
  findLessonsForSlot,
  isDayColumnInRange,
  loading = false,
  firstAvailableDate = null,
  firstTimeLoading = false,
  firstTimeChecked = false,
  onGoToNearestDate,
  isEmpty = false,
}) => {
  const [currentColumnIndex, setCurrentColumnIndex] = useState(0);
  const isMobile = useMediaQuery("(max-width: 980px)");

  const isSingleColumn = columns.length === 1;
  const displayedColumns =
    isMobile && columns.length > 1 ? [columns[Math.min(currentColumnIndex, columns.length - 1)]] : columns;

  const safeIndex = Math.min(currentColumnIndex, Math.max(columns.length - 1, 0));
  const canGoPrevious = safeIndex > 0;
  const canGoNext = safeIndex < columns.length - 1;

  const gridTemplateColumns = `${TIME_COLUMN_WIDTH} repeat(${displayedColumns.length}, minmax(160px, 1fr))`;

  const renderHeader = () => {
    if (isMobile && columns.length > 1) {
      const dayKey = columns[safeIndex];
      return (
        <div className="swimming-weekly-grid__header--mobile-nav">
          <div className="swimming-weekly-grid__mobile-nav">
            <EAIconButton
              title="Poprzedni"
              icon={<MdChevronLeft />}
              variant="icon"
              onClick={() => setCurrentColumnIndex((i) => Math.max(0, i - 1))}
              disabled={!canGoPrevious}
            />
            <div className="swimming-weekly-grid__mobile-nav-content">
              <span className="swimming-weekly-grid__mobile-nav-name">{getDayHeader(dayKey)}</span>
              <span className="swimming-weekly-grid__mobile-nav-counter">
                {safeIndex + 1}/{columns.length}
              </span>
            </div>
            <EAIconButton
              title="Następny"
              icon={<MdChevronRight />}
              variant="icon"
              onClick={() => setCurrentColumnIndex((i) => Math.min(columns.length - 1, i + 1))}
              disabled={!canGoNext}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="swimming-weekly-grid__header" style={{ gridTemplateColumns }}>
        <div className="swimming-weekly-grid__time-header">Godzina</div>
        {displayedColumns.map((dayKey) => (
          <div key={dayKey} className="swimming-weekly-grid__day-header">
            <span className="swimming-weekly-grid__day-name">{getDayHeader(dayKey)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <LoadableComponent loading={loading}>
      {isEmpty && !loading ? (
        <div className="swimming-weekly-grid__empty-state">
          <p className="swimming-weekly-grid__empty-state-text">Brak zajęć dla wybranych filtrów.</p>
          {(firstTimeLoading || firstTimeChecked) && (
            <div className="swimming-weekly-grid__empty-state-actions">
              {firstAvailableDate && firstTimeChecked && (
                <p className="swimming-weekly-grid__empty-state-hint">
                  Najbliższy dostępny termin: {format(firstAvailableDate, "d MMMM yyyy", { locale: pl })}
                </p>
              )}
              <EAButton
                title={
                  firstTimeLoading
                    ? "Szukam terminu..."
                    : firstAvailableDate
                      ? "Wyszukaj najbliższą datę"
                      : "Nie znaleziono terminów w ciągu najbliższego roku"
                }
                onClick={onGoToNearestDate ?? (() => {})}
                disabled={!firstAvailableDate || firstTimeLoading}
                loading={firstTimeLoading}
                loadingText="Szukam terminu..."
                variant="outlined"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="swimming-weekly-grid">
          {renderHeader()}

          <div className="swimming-weekly-grid__separator" />

          {hasPastHours && (
            <div className="swimming-weekly-grid__past-hours-row">
              <EAButton
                title="Pokaż wcześniejsze godziny"
                onClick={onShowPastHours}
                variant="caption"
                color="dark"
                compact
              />
            </div>
          )}

          <div className="swimming-weekly-grid__body">
            {visibleHours.map((hour) => (
              <div key={hour} className="swimming-weekly-grid__row" style={{ gridTemplateColumns }}>
                <div className="swimming-weekly-grid__time-cell">
                  <p className="swimming-weekly-grid__time-text">{hour}</p>
                </div>
                {displayedColumns.map((dayKey) => {
                  const inRange = isDayColumnInRange(dayKey);
                  const slotLessons = inRange ? findLessonsForSlot(dayKey, hour) : [];
                  return (
                    <div
                      key={`${dayKey}-${hour}`}
                      className={`swimming-weekly-grid__slot-cell${isSingleColumn ? " swimming-weekly-grid__slot-cell--single-column" : ""}${!inRange ? " swimming-weekly-grid__slot-cell--out-of-range" : ""}`}
                    >
                      {slotLessons.map((lesson) => (
                        <SwimmingLessonCard
                          key={`${lesson.objectDb}-${lesson.objectId}`}
                          lesson={lesson}
                          onClick={onLessonClick}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </LoadableComponent>
  );
};

export default SwimmingWeeklyGrid;
